import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { isNotNullOrUndefined } from '@frontend/util/type-guards';
import { OktaAuthStateService } from '@okta/okta-angular';
import { AuthService } from 'auth';
import { EnvService } from 'frontend-environment';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';

export const PROD_DOMAIN = 'demandscienceplatform.com';
export type FullStoryLogLevelTypes = 'log' | 'info' | 'warn' | 'debug';

export interface FullStoryApi {
  event(eventName: string, eventProperties: Record<string, unknown>): void;

  identify(
    uid: string | boolean,
    userVars?: Record<string, unknown> | undefined,
  ): void;

  setUserVars(user: Record<string, unknown>): void;

  getCurrentSession(): string;

  getCurrentSessionURL(): string;

  log(logLevel: string, message: string): void;
}

export type FullStoryEnv = {
  APP_DOMAIN: string;
};

@Injectable({
  providedIn: 'root',
})
export class FullStoryService {
  private window = inject(Window);
  private document = inject<Document>(DOCUMENT);
  private envService = inject<EnvService<FullStoryEnv>>(EnvService);
  private oktaAuth = inject(OktaAuthStateService, { optional: true })!;
  private authService = inject(AuthService, { optional: true })!;

  public fullStoryLoadStatus = new BehaviorSubject(false);

  private _logBuffer = new Subject<{
    level: FullStoryLogLevelTypes;
    message: string;
  }>();
  private readonly _sub$: Subscription;

  get fullStory(): FullStoryApi | undefined {
    return (this.window as any)['FS'];
  }

  public get apiIsUnavailable(): boolean {
    return !this.fullStory;
  }

  constructor() {
    const authService = this.authService;

    if (this.envService.APP_DOMAIN === PROD_DOMAIN) {
      (this.window as any)['_fs_ready'] = () =>
        this.fullStoryLoadStatus.next(true);

      if (this._sub$) {
        this._sub$.unsubscribe();
      }

      this._sub$ = this._logBuffer.subscribe(({ level, message }) => {
        if (this.fullStory) {
          this.fullStory.log(level, message);
        }
      });

      // if we have the TsAuthService, ie. We are logged into an app and not just platform home, use it. Otherwise use the out of the box OktaAuthStateService
      if (authService) {
        of({})
          .pipe(
            tap(() => {
              this.injectSnippet();
            }),
            switchMap(() =>
              combineLatest([
                this.authService.maybeHubUserData$,
                this.authService.actualUserOktaData$,
              ]),
            ),
            tap(([hubUserData, actualUserOktaData]) => {
              if (hubUserData && hubUserData.userId)
                this.identify(hubUserData.userId, {
                  displayName: actualUserOktaData?.name || hubUserData.username,
                  email: hubUserData.email || actualUserOktaData?.email,
                });
            }),
            take(1),
          )
          .subscribe();
      } else {
        of({})
          .pipe(
            tap(() => {
              this.injectSnippet();
            }),
            switchMap(() => this.oktaAuth.authState$),
            map((authState) => authState.idToken?.claims),
            filter(isNotNullOrUndefined),
            tap((oktaData) => {
              this.identify(oktaData.sub, {
                displayName: oktaData?.name,
                email: oktaData.email,
              });
            }),
            take(1),
          )
          .subscribe();
      }
    }
  }

  // This method is used to make the service look like it is used in the app so a linter won't remove it.
  dummy() {} // eslint-disable-line @typescript-eslint/no-empty-function

  /**
   * Injects the FullStory snippet into the page
   */
  public injectSnippet(): void {
    const scriptElement = this.document.createElement('script');
    const inlineScript = this.document.createTextNode(`
      window['_fs_debug'] = false;
      window['_fs_host'] = 'fullstory.com';
      window['_fs_script'] = 'edge.fullstory.com/s/fs.js';
      window['_fs_org'] = 'NBGR';
      window['_fs_namespace'] = 'FS';
      (function(m,n,e,t,l,o,g,y){
          if (e in m) {if(m.console && m.console.log)
           { m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].');} return;}
          g=m[e]=function(a,b,s){g.q?g.q.push([a,b,s]):g._api(a,b,s);};g.q=[];
          o=n.createElement(t);o.async=1;o.crossOrigin='anonymous';o.src='https://'+_fs_script;
          y=n.getElementsByTagName(t)[0];y.parentNode.insertBefore(o,y);
          g.identify=function(i,v,s){g(l,{uid:i},s);if(v)g(l,v,s)};
          g.setUserVars=function(v,s){g(l,v,s)};g.event=function(i,v,s){g('event',{n:i,p:v},s)};
          g.anonymize=function(){g.identify(!!0)};
          g.shutdown=function(){g("rec",!1)};g.restart=function(){g("rec",!0)};
          g.log = function(a,b){g("log",[a,b])};
          g.consent=function(a){g("consent",!arguments.length||a)};
          g.identifyAccount=function(i,v){o='account';v=v||{};v.acctId=i;g(o,v)};
          g.clearUserCookie=function(){};
          g.setVars=function(n, p){g('setVars',[n,p]);};
          g._w={};y='XMLHttpRequest';g._w[y]=m[y];y='fetch';g._w[y]=m[y];
          if(m[y])m[y]=function(){return g._w[y].apply(this,arguments)};
          g._v="1.3.0";
      })(window,document,window['_fs_namespace'],'script','user');
    `);

    scriptElement.appendChild(inlineScript);
    this.document.body.appendChild(scriptElement);
  }

  /**
   * Identify the active user for the current FullStory session
   *
   * @param uid - A unique user ID
   * @param userVars - Additional user information as key/value pairs
   */
  public identify(uid: string, userVars?: Record<string, unknown>): void {
    if (this.apiIsUnavailable || !this.fullStory) {
      return;
    }
    this.fullStory.identify(uid, userVars);
  }

  /**
   * Clears the identity of the active user for the current FullStory session
   */
  public clearIdentity(): void {
    if (this.apiIsUnavailable || !this.fullStory) {
      return;
    }

    this.fullStory.identify(false);
  }

  /**
   * Records custom user data for the current FullStory session
   *
   * @param userVars - Object with key/value pairs of data to be stored
   */
  public setUserVars(userVars: Record<string, unknown>): void {
    if (this.apiIsUnavailable || !this.fullStory) {
      return;
    }

    this.fullStory.setUserVars(userVars);
  }

  /**
   * Returns the ID for the current FullStory session, or null if one is
   * not available.
   *
   * @return ID of current FullStory session
   */
  public getCurrentSession(): string | undefined {
    if (this.apiIsUnavailable || !this.fullStory) {
      return;
    }

    return this.fullStory.getCurrentSession();
  }

  /**
   * Silenty sends messages to the FullStory console
   *
   * @param logLevel - Level of the message to log; see `FullStoryLogLevelTypes`
   * @param message - Message to log
   */
  public log(logLevel: FullStoryLogLevelTypes, message: string): void {
    this._logBuffer.next({ level: logLevel, message });
  }

  /**
   * Domain-specific events recorded by FullStory add additional intelligence
   * when you're searching across sessions and creating new user segments.
   *
   * @param eventName
   * @param eventProperties
   */
  sendEvent(
    eventName: string,
    eventProperties: Record<string, unknown> = {},
  ): void {
    if (this.apiIsUnavailable || !this.fullStory) {
      return;
    }

    return this.fullStory.event(eventName, eventProperties);
  }
}
